import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';

function AdminSalesPage() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { token } = useAuth();

  const limit = 50; // Показываем по 50 записей на странице

  useEffect(() => {
    const fetchSales = async () => {
      setLoading(true);
      setError(null);
      try {
        const offset = (page - 1) * limit;
        const response = await fetch(`/api/admin/sales?limit=${limit}&offset=${offset}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) {
          throw new Error('Не удалось загрузить продажи');
        }
        
        const data = await response.json();
        const salesList = Array.isArray(data) ? data : [];
        setSales(salesList);
        
        // Если получили меньше записей, чем лимит, значит это последняя страница
        if (salesList.length < limit) {
          setTotalPages(page);
        } else {
          // Предполагаем, что есть еще страницы (можно улучшить, добавив total в API)
          setTotalPages(page + 1);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchSales();
    }
  }, [token, page]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount, currency) => {
    const currencySymbols = {
      'USD': '$',
      'UAH': '₴',
      'RUB': '₽'
    };
    const symbol = currencySymbols[currency] || currency;
    return `${symbol}${parseFloat(amount).toFixed(2)}`;
  };

  const getProviderName = (provider) => {
    const names = {
      'crystalpay': 'CrystalPay',
      'heleket': 'Heleket',
      'yookassa': 'YooKassa',
      'platega': 'Platega',
      'mulenpay': 'Mulenpay',
      'urlpay': 'UrlPay',
      'telegram_stars': 'Telegram Stars',
      'balance': 'Баланс'
    };
    return names[provider] || provider || 'N/A';
  };

  if (loading && sales.length === 0) {
    return (
      <main className="admin-page-content">
        <section>
          <div className="loading-mini">Загрузка отчетов по продажам...</div>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className="admin-page-content">
        <section>
          <div className="message-error">{error}</div>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-page-content">
      <section>
        <h2>Отчеты по продажам</h2>
        <p className="admin-description-text">
          Здесь отображаются все успешные продажи с подробной информацией о каждой транзакции.
        </p>

        {sales.length === 0 ? (
          <div className="loading-mini">Продажи не найдены</div>
        ) : (
          <>
            <div className="sales-table-wrapper">
              <table className="sales-table">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Пользователь</th>
                    <th>Тариф</th>
                    <th>Сумма</th>
                    <th>Провайдер</th>
                    <th>Промокод</th>
                    <th>Order ID</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => (
                    <tr key={sale.id}>
                      <td>{formatDate(sale.date)}</td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ fontWeight: 500 }}>
                            {sale.user?.email || 'N/A'}
                          </span>
                          {sale.user?.telegram_username && (
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                              @{sale.user.telegram_username}
                            </span>
                          )}
                          {sale.user?.telegram_id && (
                            <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                              ID: {sale.user.telegram_id}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ fontWeight: 500 }}>
                            {sale.tariff?.name || 'N/A'}
                          </span>
                          {sale.tariff?.duration_days && (
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                              {sale.tariff.duration_days} дн.
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ fontWeight: 600 }}>
                        {formatAmount(sale.amount, sale.currency)}
                      </td>
                      <td>
                        <span className="provider-badge">
                          {getProviderName(sale.payment_provider)}
                        </span>
                      </td>
                      <td>
                        {sale.promo_code ? (
                          <span className="promo-badge">{sale.promo_code}</span>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}>—</span>
                        )}
                      </td>
                      <td>
                        <span className="code-cell" style={{ fontSize: '11px' }}>
                          {sale.order_id || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Пагинация */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginTop: '24px',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                Страница {page} {totalPages > 1 && `из ${totalPages}`}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                  style={{ width: 'auto', padding: '8px 16px' }}
                >
                  Назад
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setPage(p => p + 1)}
                  disabled={sales.length < limit || loading}
                  style={{ width: 'auto', padding: '8px 16px' }}
                >
                  Вперед
                </button>
              </div>
            </div>
          </>
        )}
      </section>
    </main>
  );
}

export default AdminSalesPage;

